import React from "react";
import { SectionHeader, Reveal, Pill } from "@/components/Bits";
import { EXPERIENCE } from "@/data/portfolio";
import { Briefcase, MapPin, Calendar, ChevronRight } from "lucide-react";

export default function Experience() {
  return (
    <div data-testid="experience-page" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24">
      <SectionHeader
        eyebrow="Experience"
        title="From wireframes to"
        emphasis="shipped impact."
        sub="A timeline of the products I've helped build, grow and ship — with the metrics that mattered."
      />

      <div className="relative">
        <div className="hidden md:block absolute left-[18px] top-3 bottom-3 w-px bg-[var(--border-soft)]" />

        <div className="space-y-10">
          {EXPERIENCE.map((exp, idx) => (
            <Reveal key={exp.company} delay={idx * 0.08}>
              <div className="relative md:pl-16">
                <div
                  className="hidden md:flex absolute left-0 top-6 w-10 h-10 rounded-full items-center justify-center"
                  style={{ background: `var(--${exp.accent}-soft)` }}
                >
                  <Briefcase className="w-4 h-4" />
                </div>

                <div className="soft-card p-7 md:p-10" data-testid={`exp-card-${idx}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Pill variant={exp.accent}>{exp.company}</Pill>
                      <h3 className="mt-4 font-display text-3xl md:text-4xl font-medium leading-tight">{exp.role}</h3>
                    </div>
                    <div className="flex flex-col gap-1.5 items-start md:items-end text-sm text-[var(--text-muted)] font-medium">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{exp.period}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{exp.location}</span>
                    </div>
                  </div>

                  {exp.summary && (
                    <p className="mt-5 text-base md:text-lg text-[var(--text-muted)] leading-relaxed max-w-3xl">
                      {exp.summary}
                    </p>
                  )}

                  <div className="mt-8 grid md:grid-cols-2 gap-5">
                    {exp.tracks.map((t) => (
                      <div key={t.title} className="rounded-2xl border border-[var(--border-soft)] bg-[var(--bg-2)] p-5">
                        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Track</p>
                        <h4 className="mt-1.5 font-display text-lg font-medium">{t.title}</h4>
                        <ul className="mt-4 space-y-3">
                          {t.points.map((p) => (
                            <li key={p} className="flex gap-3 text-[15px] leading-relaxed">
                              <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0 text-[var(--peach)]" />
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Quick stats */}
      <div className="mt-20 soft-card p-2 grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--border-soft)]" data-testid="exp-stats">
        {[
          { v: "1.5+", l: "Years of PM experience" },
          { v: "17%", l: "Retention lift @ Wishfy" },
          { v: "₹1.97L", l: "Monthly collections" },
          { v: "217+", l: "User interviews" },
        ].map((s) => (
          <div key={s.l} className="p-6 md:p-8 text-center md:text-left">
            <div className="font-display text-3xl md:text-5xl font-light text-[var(--text)]">{s.v}</div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
